import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack, Text, Input, NumberInput, NumberInputField, Select, Tabs, TabList, Tab, TabPanels, TabPanel } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { useBlockStore } from '../../state/commandAuxStores';
import { AppDialog, DialogButton } from '../common/AppDialog';

/**
 * ATTMAN dialog (Faz 8.10, OCS attribute_editor parity): edits ONE selected
 * block reference. Attribute tab (tag/prompt/value per definition tag,
 * CONSTANT attributes locked), TextOptions tab (height/rotation per tag),
 * Properties tab (entity layer/color). Apply → ONE undoable modify.
 *
 * BATTORDER stays out (OCS ships without it too — parity note).
 */

interface AttRowState {
    tag: string;
    prompt: string;
    value: string;
    constant: boolean;
    height: number | null;
    rotation: number | null;
}

export function AttManDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'attman');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entities = useAppStore((s) => s.entities);
    const layers = useAppStore((s) => s.layers);
    const defs = useBlockStore((s) => s.defs);
    // Single-INSERT contract (OCS attribute_editor): first selected insert.
    const insert = selectedIds.map((id) => entities[id]).find((e) => e?.type === 'insert');
    const blockName = String(insert?.data.blockName ?? '');
    const def = defs[blockName];
    const [rows, setRows] = useState<Record<string, AttRowState> | null>(null);
    const [layer, setLayer] = useState<string | null>(null);
    const [color, setColor] = useState<string | null>(null);

    // Rows initialize lazily from the definition + instance values so the
    // dialog re-reads when a different INSERT is selected.
    const key = insert?.id ?? '';
    const [rowKey, setRowKey] = useState('');
    if (isOpen && insert && def && rowKey !== key) {
        setRowKey(key);
        const values = (insert.data.attributeValues ?? {}) as Record<string, string>;
        const overrides = (insert.data.attributeOverrides ?? {}) as Record<string, { height?: number; rotation?: number } | undefined>;
        const next: Record<string, AttRowState> = {};
        def.attributes.forEach((a) => {
            const tag = String(a.tag ?? '');
            next[tag] = {
                tag,
                prompt: String(a.prompt ?? ''),
                value: values[tag] ?? String(a.defaultValue ?? ''),
                constant: false,
                height: overrides[tag]?.height ?? null,
                rotation: overrides[tag]?.rotation ?? null,
            };
        });
        setRows(next);
        setLayer(insert.layer);
        setColor(String((insert.data.color as string) ?? ''));
    }
    if (!isOpen || !insert || !def) {
        return (
            <AppDialog isOpen={isOpen} onClose={onClose} title={t('ui.cad.attman.title')} size="md"
                footer={<DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>}>
                <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.attman.noInsert')}</Text>
            </AppDialog>
        );
    }

    const patch = (tag: string, update: Partial<AttRowState>) => {
        setRows((prev) => (prev ? { ...prev, [tag]: { ...prev[tag], ...update } } : prev));
    };

    const apply = () => {
        if (!rows) return;
        const values: Record<string, string> = {};
        const overrides: Record<string, { height?: number; rotation?: number }> = {};
        Object.values(rows).forEach((r) => {
            values[r.tag] = r.value;
            if (r.height !== null || r.rotation !== null) {
                overrides[r.tag] = { height: r.height ?? undefined, rotation: r.rotation ?? undefined };
            }
        });
        const targetId = insert.id;
        const before = structuredClone(insert);
        const nextData: Record<string, unknown> = { ...insert.data, attributeValues: values, attributeOverrides: overrides };
        if (color) nextData.color = color;
        const updates: Record<string, unknown> = { data: nextData };
        if (layer && layers[layer]) updates.layer = layer;
        const after = { ...insert, ...updates } as typeof insert;
        useAppStore.getState().updateEntity(targetId, updates as never);
        useAppStore.getState().pushHistory({
            type: 'cmd.modify',
            timestamp: Date.now(),
            data: { count: 1, label: 'ATTMAN' },
            undo: () => { useAppStore.getState().updateEntity(targetId, before as never); },
            redo: () => { useAppStore.getState().updateEntity(targetId, structuredClone(after) as never); },
        });
        onClose();
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={`${t('ui.cad.attman.title')} — ${blockName}`}
            size="lg"
            footer={
                <HStack justify="flex-end" w="full" spacing={2}>
                    <DialogButton variant="primary" onClick={apply}>{t('ui.cad.attman.apply')}</DialogButton>
                    <DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>
                </HStack>
            }
        >
            <Tabs size="sm" variant="enclosed" colorScheme="blue">
                <TabList>
                    <Tab>{t('ui.cad.attman.tabAttribute')}</Tab>
                    <Tab>{t('ui.cad.attman.tabTextOptions')}</Tab>
                    <Tab>{t('ui.cad.attman.tabProperties')}</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel px={0}>
                        <VStack spacing={2} align="stretch">
                            {Object.values(rows ?? {}).map((r) => (
                                <HStack key={r.tag} justify="space-between" spacing={3}>
                                    <Text fontSize="sm" w="100px" flexShrink={0} fontFamily="mono">{r.tag}</Text>
                                    <Text fontSize="xs" w="140px" flexShrink={0} color="var(--color-text-secondary)" isTruncated>
                                        {r.prompt || '—'}
                                    </Text>
                                    <Input
                                        size="sm" flex={1} value={r.value} isDisabled={r.constant}
                                        onChange={(e) => patch(r.tag, { value: e.target.value })}
                                        bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                                    />
                                </HStack>
                            ))}
                            <Text fontSize="xs" color="var(--color-text-secondary)">{t('ui.cad.attman.constantLocked')}</Text>
                        </VStack>
                    </TabPanel>
                    <TabPanel px={0}>
                        <VStack spacing={2} align="stretch">
                            {Object.values(rows ?? {}).map((r) => (
                                <HStack key={r.tag} justify="space-between" spacing={3}>
                                    <Text fontSize="sm" w="100px" flexShrink={0} fontFamily="mono">{r.tag}</Text>
                                    <NumberInput size="sm" w="110px" value={r.height ?? 0.25} min={0.05} step={0.05}
                                        onChange={(_, v) => patch(r.tag, { height: Number.isFinite(v) && v > 0 ? v : null })}>
                                        <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                                    </NumberInput>
                                    <NumberInput size="sm" w="110px" value={r.rotation ?? 0} step={15}
                                        onChange={(_, v) => patch(r.tag, { rotation: Number.isFinite(v) ? v : null })}>
                                        <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                                    </NumberInput>
                                </HStack>
                            ))}
                        </VStack>
                    </TabPanel>
                    <TabPanel px={0}>
                        <VStack spacing={2} align="stretch">
                            <HStack justify="space-between">
                                <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.attman.layer')}</Text>
                                <Select size="sm" w="200px" value={layer ?? insert.layer}
                                    onChange={(e) => setLayer(e.target.value)}>
                                    {Object.values(layers).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </Select>
                            </HStack>
                            <HStack justify="space-between">
                                <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.attman.color')}</Text>
                                <Input size="sm" w="200px" value={color ?? ''} fontFamily="mono"
                                    bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                                    onChange={(e) => setColor(e.target.value)} />
                            </HStack>
                        </VStack>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </AppDialog>
    );
}
